import { Bookmark, ChevronLeft, ChevronRight, Flag, TrashIcon } from "lucide-react";

type SwipeBarProps = {
  side: "left" | "right";
  muted?: boolean;
  actionType?: "bookmark" | "flag" | "delete";
  count?: number;
  onClick?: () => void;
  isProcessing?: boolean;
  /** オンボーディングツアーのハイライト対象を示す data-tour 属性値 */
  dataTour?: string;
};

/**
 * 数字を人間が読みやすい形式にフォーマット
 * 例: 1234 -> "1.2k", 10000 -> "10k"
 */
const formatCount = (count: number): string => {
  if (count < 1000) {
    return count.toString();
  } else if (count < 10000) {
    // 1000-9999: 小数点1桁で表示（例: 1.2k）
    return (count / 1000).toFixed(1) + "k";
  } else if (count < 1000000) {
    // 10000-999999: 整数で表示（例: 10k）
    return Math.floor(count / 1000) + "k";
  } else {
    // 1000000以上: M表記（例: 1.2M）
    return (count / 1000000).toFixed(1) + "M";
  }
};

export const SwipeBar = ({
  side,
  muted = false,
  actionType,
  count,
  onClick,
  isProcessing = false,
  dataTour
}: SwipeBarProps) => {
  const isLeft = side === "left";
  const ChevronComponent = isLeft ? ChevronLeft : ChevronRight;

  // デフォルトのアイコン（既存の動作）
  let IconComponent = isLeft ? Flag : Bookmark;

  // actionTypeが指定されている場合は、そのアイコンを使用
  if (actionType === "bookmark") {
    IconComponent = Bookmark;
  } else if (actionType === "flag") {
    IconComponent = Flag;
  } else if (actionType === "delete") {
    IconComponent = TrashIcon;
  }

  // 削除アクションの場合はグレー、それ以外は緑色
  const iconColor =
    actionType === "delete"
      ? muted
        ? "text-gray-400"
        : "text-gray-500"
      : muted
      ? "text-gray-400"
      : "text-[#0fff78]";

  return (
    <div
      data-tour={dataTour}
      className={`flex items-center px-2 ${onClick ? "cursor-pointer" : ""} ${
        isProcessing ? "opacity-50" : ""
      }`}
      onClick={!isProcessing ? onClick : undefined}
    >
      <div className="flex h-16 w-6 flex-col items-center justify-center p-1">
        <IconComponent
          size={16}
          className={`-mb-0.5 ${iconColor} ${isProcessing ? "animate-pulse" : ""}`}
          fill="currentColor"
        />
        {count !== undefined && (
          <div className={`mb-1 text-xs font-semibold ${iconColor}`}>{formatCount(count)}</div>
        )}
        <ChevronComponent size={12} className={iconColor} />
      </div>
    </div>
  );
};
